"use client";
// apps/vrw-cp
// 衛星の3D軌道を表示するコンポーネント
import React, { useEffect, useRef } from "react";
import { Typography } from "@mui/material";
import {
  Viewer,
  Cartesian3,
  Color,
  LabelStyle,
  VerticalOrigin,
  HorizontalOrigin,
  SampledPositionProperty,
  JulianDate,
  HermitePolynomialApproximation,
  ClockRange,
  CallbackProperty,
  Ion,
  ShadowMode,
  SunLight,
} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import * as satellite from "satellite.js";

/**
 * 座標変換関数を生成する
 * @param useEarthFixed 地表固定座標を使用するか
 */
const createPositionConverter = (
  useEarthFixed: boolean,
): ((posEci: any, date: Date) => Cartesian3 | undefined) => {
  const baseGmst = satellite.gstime(new Date());
  return (posEci: any, date: Date) => {
    try {
      const gmst = useEarthFixed ? satellite.gstime(date) : baseGmst;
      const posGd = satellite.eciToGeodetic(posEci, gmst);
      
      // より厳密な検証：有限数かつ妥当な範囲内かチェック
      if (
        !Number.isFinite(posGd.longitude) ||
        !Number.isFinite(posGd.latitude) ||
        !Number.isFinite(posGd.height) ||
        Math.abs(posGd.longitude) > Math.PI * 2 ||
        Math.abs(posGd.latitude) > Math.PI / 2 ||
        posGd.height < -10000 || // 地下10km以下は無効
        posGd.height > 100000000 // 100,000km以上は無効
      ) {
        // 座標が妥当な範囲外の場合、fallback処理
        // ECIを直接Cartesian3に変換（慣性座標用フォールバック）
        return new Cartesian3(posEci.x * 1000, posEci.y * 1000, posEci.z * 1000);
      }
      
      return Cartesian3.fromDegrees(
        posGd.longitude * (180 / Math.PI),
        posGd.latitude * (180 / Math.PI),
        posGd.height * 1000,
      );
    } catch (error) {
      // Position conversion failed, using ECI fallback
      // 変換に失敗した場合のフォールバック
      return new Cartesian3(posEci.x * 1000, posEci.y * 1000, posEci.z * 1000);
    }
  };
};

// 複数の衛星に使用する色の配列 - すべて赤色に統一
const SATELLITE_COLORS = [Color.RED];

// 軌道の色を統一
const ORBIT_COLORS = [
  "#FFFFFF", // 白 - 過去軌道
  "#FFFF00", // 黄 - 未来軌道
];

if (process.env.NEXT_PUBLIC_CESIUM_ION_TOKEN?.length) {
  Ion.defaultAccessToken = process.env.NEXT_PUBLIC_CESIUM_ION_TOKEN;
}

// 軌道計算関数 - キャッシュ付き
/**
 * 軌道計算関数を生成する
 * @param satrec 衛星レコード
 * @param mode 軌道モード ('past-half', 'future-half', boolean)
 * @param useEarthFixed 地表固定座標を使用するか
 */
const createOrbitCalculator = (
  satrec: any,
  mode: boolean | 'complete' | 'past-half' | 'future-half',
  useEarthFixed: boolean,
) => {
  let cachedPositions: Cartesian3[] = [];
  let lastUpdateTime = 0;
  const UPDATE_INTERVAL = 15000; // 15秒ごとに更新
  const NUM_STEPS = 100; // 軌道全体を描画するステップ数
  const convert = createPositionConverter(useEarthFixed);

  return (time: any) => {
    const currentTime = JulianDate.toDate(time);
    const now = currentTime.getTime();

    if (
      now - lastUpdateTime > UPDATE_INTERVAL ||
      cachedPositions.length === 0
    ) {
      const positions: Cartesian3[] = [];

      // 軌道周期を計算 (ケプラーの法則)
      const earthRadius = 6378.137; // 地球半径 (km)
      const mu = 398600.4418; // 地球の標準重力パラメータ (km^3/s^2)
      const semiMajorAxis = satrec.a * earthRadius; // 軌道半長軸 (km)
      const orbitalPeriod =
        2 * Math.PI * Math.sqrt(Math.pow(semiMajorAxis, 3) / mu); // 軌道周期 (秒)
      
      let startTime, endTime;
      
      if (mode === 'complete') {
        // 完全軌道：1周分を連続して表示（慣性座標用）
        startTime = -orbitalPeriod / 2;
        endTime = orbitalPeriod / 2;
      } else if (useEarthFixed) {
        // 地表固定座標：従来通り過去軌道と未来軌道を分けて表示
        startTime = 0;
        endTime = orbitalPeriod;
      } else {
        // その他の場合（慣性座標で分割表示）
        const halfPeriod = orbitalPeriod / 2;
        if (mode) {
          startTime = -halfPeriod;
          endTime = 0;
        } else {
          startTime = 0;
          endTime = halfPeriod;
        }
      }

      // 完全軌道の場合、より密なサンプリングを行う
      const actualSteps = mode === 'complete' ? NUM_STEPS * 2 : NUM_STEPS;
      const actualStepSeconds = (endTime - startTime) / actualSteps;
      
      for (let i = 0; i <= actualSteps; i++) {
        const t = startTime + (i * actualStepSeconds);
        
        try {
          let time;
          if (mode === 'complete') {
            // 完全軌道：時刻を直接加算
            time = new Date(currentTime.getTime() + t * 1000);
          } else if (useEarthFixed) {
            // 地表固定座標：従来の計算方法
            const sign = mode ? -1 : 1; // modeがtrueなら過去軌道
            time = new Date(currentTime.getTime() + sign * t * 1000);
          } else {
            // その他の場合（慣性座標の分割表示）：時刻を直接加算
            time = new Date(currentTime.getTime() + t * 1000);
          }
          
          const posVel = satellite.propagate(satrec, time);
          if (posVel && posVel.position) {
            const posEci = posVel.position;
            const position = convert(posEci, time);

            if (position && Number.isFinite(position.x) && Number.isFinite(position.y) && Number.isFinite(position.z)) {
              positions.push(position);
            }
          }
        } catch (e) {
          // エラーを無視して次のポイントへ続行
          continue;
        }
      }
      
      // 完全軌道の場合、最初の点を最後に追加して閉じた軌道にする
      if (mode === 'complete' && positions.length > 0) {
        positions.push(positions[0]);
      }


      cachedPositions = positions;
      lastUpdateTime = now;
    }

    return cachedPositions;
  };
};

// 遠点と近点を計算する関数
const calculateApogeeAndPerigee = (satrec: any) => {
  const earthRadius = 6378.137; // 地球半径 (km)

  // satrec.a と satrec.ecco の値を検証
  if (!satrec.a || !satrec.ecco || isNaN(satrec.a) || isNaN(satrec.ecco)) {
    console.error("Invalid satrec values for apogee and perigee calculation:", {
      a: satrec.a,
      ecco: satrec.ecco,
    });
    return { apogee: NaN, perigee: NaN };
  }

  // 軌道半長軸をキロメートル単位に変換
  const semiMajorAxis = satrec.a * earthRadius;

  // 遠点と近点を計算
  const apogee = semiMajorAxis * (1 + satrec.ecco) - earthRadius; // 遠点 (km)
  const perigee = semiMajorAxis * (1 - satrec.ecco) - earthRadius; // 近点 (km)

  return { apogee, perigee };
};

// 軌道上の特殊点（遠地点・近地点・昇交点・降交点）を計算する関数
const calculateOrbitalPoints = (satrec: any, currentTime: Date) => {
  const earthRadius = 6378.137; // 地球半径 (km)
  const semiMajorAxis = satrec.a * earthRadius;
  const eccentricity = satrec.ecco || 0;
  const inclination = satrec.inclo || 0;

  // 軌道周期を計算
  const mu = 398600.4418; // 地球の標準重力パラメータ (km^3/s^2)
  const orbitalPeriod = 2 * Math.PI * Math.sqrt(Math.pow(semiMajorAxis, 3) / mu);

  const points: Array<{
    type: 'perigee' | 'apogee' | 'ascending' | 'descending';
    name: string;
    position: any;
    altitude: number;
    inclination: number;
  }> = [];

  // 軌道を100点でサンプリングして特殊点を探す
  const numSamples = 100;
  const sampleData: {
    time: Date;
    position: any;
    distance: number;
    altitude: number;
    z: number;
    index: number;
  }[] = [];
  
  for (let i = 0; i < numSamples; i++) {
    const timeOffset = (i / numSamples) * orbitalPeriod * 1000;
    const sampleTime = new Date(currentTime.getTime() + timeOffset);
    const posVel = satellite.propagate(satrec, sampleTime);
    
    if (posVel && posVel.position) {
      const posEci = posVel.position;
      // 地心距離を計算
      const distance = Math.sqrt(posEci.x * posEci.x + posEci.y * posEci.y + posEci.z * posEci.z);
      const altitude = distance - earthRadius;
      
      sampleData.push({
        time: sampleTime,
        position: posEci,
        distance: distance,
        altitude: altitude,
        z: posEci.z, // Z座標（昇交点・降交点判定用）
        index: i,
      });
    }
  }

  if (sampleData.length === 0) return points;

  // 遠地点（最大距離）を見つける
  let maxDistance = -Infinity;
  let apogeeIndex = 0;
  
  // 近地点（最小距離）を見つける
  let minDistance = Infinity;
  let perigeeIndex = 0;

  sampleData.forEach((sample, index) => {
    if (sample.distance > maxDistance) {
      maxDistance = sample.distance;
      apogeeIndex = index;
    }
    if (sample.distance < minDistance) {
      minDistance = sample.distance;
      perigeeIndex = index;
    }
  });

  // 昇交点と降交点を見つける（Z座標が0を横切る点）
  let ascendingNodeData = null;
  let descendingNodeData = null;

  // 交点計算用の補助関数
  /**
   * 2点間を線形補間して交点を求める
   * @param current 現在のサンプルデータ
   * @param next 次のサンプルデータ
   */
  const interpolateNode = (
    current: typeof sampleData[0],
    next: typeof sampleData[0],
  ) => {
    const ratio = Math.abs(current.z) / (Math.abs(current.z) + Math.abs(next.z));
    const interpolatedTime = new Date(
      current.time.getTime() + ratio * (next.time.getTime() - current.time.getTime()),
    );
    const interpolatedPosVel = satellite.propagate(satrec, interpolatedTime);

    if (interpolatedPosVel && interpolatedPosVel.position) {
      const distance = Math.sqrt(
        interpolatedPosVel.position.x * interpolatedPosVel.position.x +
          interpolatedPosVel.position.y * interpolatedPosVel.position.y +
          interpolatedPosVel.position.z * interpolatedPosVel.position.z,
      );
      return {
        time: interpolatedTime,
        position: interpolatedPosVel.position,
        distance,
        altitude: distance - earthRadius,
        z: interpolatedPosVel.position.z,
      };
    }
    return null;
  };

  for (let i = 0; i < sampleData.length - 1; i++) {
    const current = sampleData[i];
    const next = sampleData[i + 1];

    // 昇交点：Z座標が負から正に変化
    if (current.z <= 0 && next.z > 0 && !ascendingNodeData) {
      const node = interpolateNode(current, next);
      if (node) {
        ascendingNodeData = { ...node, index: i };
      }
    }

    // 降交点：Z座標が正から負に変化
    if (current.z >= 0 && next.z < 0 && !descendingNodeData) {
      const node = interpolateNode(current, next);
      if (node) {
        descendingNodeData = { ...node, index: i };
      }
    }
  }

  // ループ内で交点が見つからない場合、最後の点と最初の点も確認する
  if (!ascendingNodeData) {
    const current = sampleData[sampleData.length - 1];
    const next = sampleData[0];
    if (current.z <= 0 && next.z > 0) {
      const node = interpolateNode(current, next);
      if (node) ascendingNodeData = { ...node, index: sampleData.length - 1 };
    }
  }

  if (!descendingNodeData) {
    const current = sampleData[sampleData.length - 1];
    const next = sampleData[0];
    if (current.z >= 0 && next.z < 0) {
      const node = interpolateNode(current, next);
      if (node) descendingNodeData = { ...node, index: sampleData.length - 1 };
    }
  }

  // 近地点を追加
  if (perigeeIndex < sampleData.length) {
    const perigeeData = sampleData[perigeeIndex];
    points.push({
      type: 'perigee',
      name: '近地点',
      position: perigeeData.position,
      altitude: perigeeData.altitude,
      inclination: inclination * (180 / Math.PI),
    });
  }

  // 遠地点を追加
  if (apogeeIndex < sampleData.length) {
    const apogeeData = sampleData[apogeeIndex];
    points.push({
      type: 'apogee',
      name: '遠地点',
      position: apogeeData.position,
      altitude: apogeeData.altitude,
      inclination: inclination * (180 / Math.PI),
    });
  }

  // 昇交点を追加
  if (ascendingNodeData) {
    points.push({
      type: 'ascending',
      name: '昇交点',
      position: ascendingNodeData.position,
      altitude: ascendingNodeData.altitude,
      inclination: inclination * (180 / Math.PI),
    });
  }

  // 降交点を追加
  if (descendingNodeData) {
    points.push({
      type: 'descending',
      name: '降交点',
      position: descendingNodeData.position,
      altitude: descendingNodeData.altitude,
      inclination: inclination * (180 / Math.PI),
    });
  }

  return points;
};

const App: React.FC<{ tles?: string[][]; useEarthFixed?: boolean }> = ({
  tles = [],
  useEarthFixed = true,
}) => {
  const cesiumContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cesiumContainer.current) return;

    console.log(`Rendering ${tles.length} satellites`);
    (window as any).CESIUM_BASE_URL = "/cesium";

    const viewer = new Viewer(cesiumContainer.current, {
      terrainProvider: undefined,
      baseLayerPicker: false,
      infoBox: true,
      sceneModePicker: false,
      navigationHelpButton: false,
      animation: true,
      timeline: true,
      fullscreenButton: false,
      geocoder: false,
      homeButton: true,
      shadows: true,
    });
    
    // Store viewer globally for E2E testing
    (window as any).cesiumViewer = viewer;
    const convert = createPositionConverter(useEarthFixed);

    // 時間設定の最適化
    viewer.clock.shouldAnimate = true;
    viewer.clock.multiplier = 1;
    viewer.clock.clockRange = ClockRange.LOOP_STOP;

    // 地球の影と昼夜効果の設定
    viewer.scene.globe.enableLighting = true; // 地球の昼夜の明暗を有効化
    viewer.shadowMap.enabled = true; // 影マップを有効化
    viewer.scene.globe.shadows = ShadowMode.ENABLED; // 地球の影を有効化
    viewer.shadowMap.darkness = 1; // 影の暗さ（0〜1）
    viewer.scene.light = new SunLight(); // 太陽光の設定

    // 各衛星の情報を格納する配列
    const satelliteData: {
      entity: any;
      satrec: any;
      name: string;
      color: Color;
      pastOrbit: any;
      futureOrbit: any;
      orbitalPoints?: any[];
    }[] = [];

    // 複数の衛星をループ処理
    tles.forEach((tle, satelliteIndex) => {
      try {
        // TLE データの解析
        const tleLine1 = tle[1];
        const tleLine2 = tle[2];
        const satelliteName = tle[0];

        if (!tleLine1 || !tleLine2) {
          console.error(
            `Invalid TLE data for satellite ${satelliteIndex}: ${tle}`
          );
          return;
        }

        const satrec = satellite.twoline2satrec(tleLine1, tleLine2);

        if (!satrec) {
          console.error(
            `Failed to parse TLE for satellite ${satelliteIndex}: ${satelliteName}`
          );
          return;
        }

        // satrec.ecco のデフォルト値を設定
        if (satrec.ecco === undefined) {
          console.warn(
            `satrec.ecco is undefined for satellite ${satelliteName}. Setting default value to 0.`
          );
          satrec.ecco = 0; // デフォルト値を設定
        }

        // 衛星ごとの色を選択
        const satelliteColor =
          SATELLITE_COLORS[satelliteIndex % SATELLITE_COLORS.length];

        // 初期カメラ位置は最初の衛星に設定（最初のループのみ）
        if (satelliteIndex === 0) {
          const initialTime = JulianDate.now();
          const initialPosVel = satellite.propagate(
            satrec,
            JulianDate.toDate(initialTime),
          );
          if (initialPosVel && initialPosVel.position) {
            const initialCart = convert(
              initialPosVel.position,
              JulianDate.toDate(initialTime),
            );
            if (initialCart) {
              // 衛星の高度を取得して適切なカメラ距離を計算
              const satelliteHeight = Cartesian3.magnitude(initialCart);
              const earthRadius = 6371000; // 地球半径 (m)
              const altitude = satelliteHeight - earthRadius;
              
              // 適切な視点距離を計算（衛星高度の1.5倍程度、最小500km）
              const viewDistance = Math.max(altitude * 1.5, 500000);
              
              // 衛星から地球中心への方向ベクトルを正規化
              const direction = Cartesian3.normalize(initialCart, new Cartesian3());
              
              // カメラ位置を衛星から適切な距離に配置
              const cameraPosition = Cartesian3.multiplyByScalar(
                direction, 
                satelliteHeight + viewDistance, 
                new Cartesian3()
              );
              
              viewer.camera.flyTo({
                destination: cameraPosition,
                orientation: {
                  direction: Cartesian3.negate(direction, new Cartesian3()),
                  up: Cartesian3.UNIT_Z
                }
              });
            }
          }
        }

        let pastOrbit, futureOrbit;
        
        if (useEarthFixed) {
          // 地表固定座標：従来通り過去軌道と未来軌道を分けて表示
          const pastOrbitCalculator = createOrbitCalculator(
            satrec,
            true,
            useEarthFixed,
          );
          pastOrbit = viewer.entities.add({
            id: `past-orbit-${satelliteIndex}`,
            name: `${satelliteName} Past Orbit`,
            polyline: {
              positions: new CallbackProperty((time) => {
                return pastOrbitCalculator(time);
              }, false),
              width: 2,
              material: Color.fromCssColorString(ORBIT_COLORS[0]).withAlpha(0.8),
            },
          });

          const futureOrbitCalculator = createOrbitCalculator(
            satrec,
            false,
            useEarthFixed,
          );
          futureOrbit = viewer.entities.add({
            id: `future-orbit-${satelliteIndex}`,
            name: `${satelliteName} Future Orbit`,
            polyline: {
              positions: new CallbackProperty((time) => {
                return futureOrbitCalculator(time);
              }, false),
              width: 2,
              material: Color.fromCssColorString(ORBIT_COLORS[1]).withAlpha(0.8),
            },
          });
        } else {
          // 慣性座標：連続した完全軌道を polyline で描画
          const completeOrbitCalculator = createOrbitCalculator(
            satrec,
            'complete',
            useEarthFixed,
          );
          
          // 完全な軌道を1つのエンティティとして作成
          pastOrbit = viewer.entities.add({
            id: `complete-orbit-${satelliteIndex}`,
            name: `${satelliteName} Complete Orbit`,
            polyline: {
              positions: new CallbackProperty((time) => {
                return completeOrbitCalculator(time);
              }, false),
              width: 2,
              material: Color.fromCssColorString(ORBIT_COLORS[1]).withAlpha(0.8), // 黄色
            },
          });
          
          // 未来軌道は作成しない（完全軌道に統合）
          futureOrbit = null;
        }

        // 衛星エンティティの追加
        const satelliteEntity = viewer.entities.add({
          id: `satellite-${satelliteIndex}`,
          name: satelliteName,
          position: new SampledPositionProperty(),
          point: {
            pixelSize: 10,
            color: Color.RED,
            outlineColor: Color.WHITE,
            outlineWidth: 2,
          },
          label: {
            text: new CallbackProperty(() => {
              try {
                const now = new Date();
                const positionAndVelocity = satellite.propagate(satrec, now);

                if (positionAndVelocity && positionAndVelocity.position) {
                  const gmst = satellite.gstime(now);
                  const positionGd = satellite.eciToGeodetic(
                    positionAndVelocity.position,
                    gmst
                  );

                  const altitude = positionGd.height; // 高度 (km)
                  const latitude = positionGd.latitude * (180 / Math.PI); // 緯度 (度)
                  const longitude = positionGd.longitude * (180 / Math.PI); // 経度 (度)
                  const { apogee, perigee } = calculateApogeeAndPerigee(satrec); // 遠点と近点を計算
                  const inclination = satrec.inclo * (180 / Math.PI); // 傾斜角 (度)

                  const locationText =
                    `${latitude.toFixed(2)}°` +
                    ", " +
                    `${longitude.toFixed(2)}°`;
                  const altitudeText = `${altitude.toFixed(2)} km`;
                  const orbitText =
                    `${apogee.toFixed(2)} km` +
                    ", " +
                    `${perigee.toFixed(2)} km` +
                    ", " +
                    `${inclination.toFixed(2)}°`;
                  return `${satelliteName}\n位置: ${locationText}\n高度: ${altitudeText}\n軌道: ${orbitText}`;
                }

                return `${satelliteName}\nデータ取得中...`;
              } catch (e) {
                console.error(
                  `Error generating label for satellite: ${satelliteName}`,
                  e
                );
                return `${satelliteName}\nデータ取得中...`;
              }
            }, false),
            font: "8pt sans-serif",
            fillColor: Color.WHITE,
            outlineColor: Color.BLACK,
            outlineWidth: 2,
            style: LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: VerticalOrigin.BOTTOM,
            horizontalOrigin: HorizontalOrigin.LEFT,
            pixelOffset: new Cartesian3(0, -12, 0),
            showBackground: true,
            backgroundColor: new Color(0, 0, 0, 0.5),
          },
        });

        // 初期位置を設定
        const now = new Date();
        const initialPosVel = satellite.propagate(satrec, now);
        if (initialPosVel && initialPosVel.position) {
          const initialCart = convert(initialPosVel.position, now);
          const initialJulian = JulianDate.fromDate(now);

          if (satelliteEntity.position instanceof SampledPositionProperty) {
            satelliteEntity.position.setInterpolationOptions({
              interpolationDegree: 5,
              interpolationAlgorithm: HermitePolynomialApproximation,
            });

            // 初期位置を追加
            if (initialCart) {
              satelliteEntity.position.addSample(initialJulian, initialCart);
            }

            // 未来の位置も追加して補間を有効化
            for (let i = 1; i <= 5; i++) {
              const futureJulian = JulianDate.addSeconds(
                initialJulian,
                i * 20,
                new JulianDate()
              );
              const futureJsDate = JulianDate.toDate(futureJulian);
              const futurePosVel = satellite.propagate(satrec, futureJsDate);

              if (futurePosVel && futurePosVel.position) {
                const futureCart = convert(futurePosVel.position, futureJsDate);

                if (futureCart) {
                  satelliteEntity.position.addSample(futureJulian, futureCart);
                }
              }
            }
          }
        }

        // 軌道上の特殊点を計算して表示（慣性座標のみ）
        let orbitalPointEntities: any[] = [];
        
        if (!useEarthFixed) {
          // 慣性座標の場合のみ軌道特殊点を表示
          const orbitalPoints = calculateOrbitalPoints(satrec, new Date());
          
          console.log(`Satellite ${satelliteIndex} (${satelliteName}): Found ${orbitalPoints.length} orbital points:`, 
            orbitalPoints.map(p => p.type));
          
          orbitalPoints.forEach((point, pointIndex) => {
            const pointCart = convert(point.position, new Date());
            if (pointCart) {
              // ポイントの色とラベルを設定
              let pointColor = Color.WHITE;
              let pointSize = 6; // 衛星より小さく
              let labelText = '';
              
              switch (point.type) {
                case 'perigee':
                  pointColor = Color.LIME; // 近地点は明緑色
                  labelText = 'Pe'; // シンプルなラベル
                  break;
                case 'apogee':
                  pointColor = Color.ORANGE; // 遠地点はオレンジ色
                  labelText = 'Ap'; // シンプルなラベル
                  break;
                case 'ascending':
                  pointColor = Color.CYAN; // 昇交点はシアン色
                  labelText = '▲'; // 上向き三角
                  break;
                case 'descending':
                  pointColor = Color.MAGENTA; // 降交点はマゼンタ色
                  labelText = '▼'; // 下向き三角
                  break;
              }

              let pointEntity;
              
              if (point.type === 'ascending' || point.type === 'descending') {
                // 交点は三角形をCanvasで描画してbillboardとして表示
                const isAscending = point.type === 'ascending';
                const size = 6; // 三角形のサイズを小さく（pointSizeと同じ）
                
                // Canvas上に三角形を描画
                const canvas = document.createElement('canvas');
                canvas.width = size * 2 + 2; // 枠線分のマージンを追加
                canvas.height = size * 2 + 2;
                const ctx = canvas.getContext('2d');
                
                if (ctx) {
                  // 三角形を描画
                  ctx.fillStyle = pointColor.toCssColorString();
                  ctx.strokeStyle = 'black';
                  ctx.lineWidth = 1;
                  
                  ctx.beginPath();
                  if (isAscending) {
                    // 上向き三角形（昇交点）
                    ctx.moveTo(size + 1, 1);               // 上の頂点
                    ctx.lineTo(1, size * 2);               // 左下
                    ctx.lineTo(size * 2 + 1, size * 2);    // 右下
                  } else {
                    // 下向き三角形（降交点）
                    ctx.moveTo(size + 1, size * 2);        // 下の頂点
                    ctx.lineTo(1, 1);                      // 左上
                    ctx.lineTo(size * 2 + 1, 1);           // 右上
                  }
                  ctx.closePath();
                  ctx.fill();
                  ctx.stroke();
                }
                
                pointEntity = viewer.entities.add({
                  id: `orbital-point-${satelliteIndex}-${pointIndex}`,
                  name: `${satelliteName} ${point.name}`,
                  position: pointCart,
                  billboard: {
                    image: canvas,
                    width: size * 2 + 2,
                    height: size * 2 + 2,
                    verticalOrigin: VerticalOrigin.CENTER,
                    horizontalOrigin: HorizontalOrigin.CENTER,
                    scaleByDistance: undefined, // 距離によるスケーリングを無効化
                  },
                });
              } else {
                // 遠地点・近地点は従来通り丸点で表示
                pointEntity = viewer.entities.add({
                  id: `orbital-point-${satelliteIndex}-${pointIndex}`,
                  name: `${satelliteName} ${point.name}`,
                  position: pointCart,
                  point: {
                    pixelSize: pointSize,
                    color: pointColor,
                    outlineColor: Color.BLACK,
                    outlineWidth: 1,
                  },
                  label: {
                    text: labelText,
                    font: "9pt sans-serif",
                    fillColor: Color.WHITE,
                    outlineColor: Color.BLACK,
                    outlineWidth: 1,
                    style: LabelStyle.FILL_AND_OUTLINE,
                    verticalOrigin: VerticalOrigin.CENTER,
                    horizontalOrigin: HorizontalOrigin.LEFT,
                    pixelOffset: new Cartesian3(8, 0, 0),
                    showBackground: false,
                    show: true,
                  },
                });
              }
              
              orbitalPointEntities.push(pointEntity);
            }
          });
        }

        // 衛星データを配列に追加（軌道エンティティを含む）
        satelliteData.push({
          entity: satelliteEntity,
          satrec: satrec,
          name: satelliteName,
          color: satelliteColor,
          pastOrbit: pastOrbit,
          futureOrbit: futureOrbit || pastOrbit, // 慣性座標の場合、futureOrbitはnullなのでpastOrbitを代用
          orbitalPoints: orbitalPointEntities,
        });
      } catch (e) {
        console.error(`Error processing satellite ${satelliteIndex}: ${e}`);
      }
    });

    const BATCH_SIZE = 10; // 一度に更新する衛星の数
    let currentBatch = 0;

    // すべての衛星の位置とラベルを更新する単一のイベントリスナー
    const tickListener = viewer.clock.onTick.addEventListener(() => {
      const currentTime = viewer.clock.currentTime;
      const jsDate = JulianDate.toDate(currentTime);

      const start = currentBatch * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, satelliteData.length);

      for (let i = start; i < end; i++) {
        const { entity, satrec, name } = satelliteData[i];
        try {
          const positionAndVelocity = satellite.propagate(satrec, jsDate);
          if (positionAndVelocity && positionAndVelocity.position) {
            const positionEci = positionAndVelocity.position;
            const position = convert(positionEci, jsDate);

            if (!position) {
              console.warn(
                `Skipping satellite ${name} (Index: ${i}) due to insufficient update interval`
              );
              continue;
            }

            if (entity.position instanceof SampledPositionProperty) {
              entity.position.addSample(currentTime, position);
            }
          }
        } catch (e) {
          console.error(`Error updating satellite ${i} (${name}): ${e}`);
        }
      }

      currentBatch =
        (currentBatch + 1) % Math.ceil(satelliteData.length / BATCH_SIZE);
    });

    // 全ての衛星エンティティの読み込み完了後にズーム調整
    setTimeout(() => {
      // カメラの初期設定後に全体をフィットさせるのを避ける
      // 個別の衛星にフォーカスしたカメラ位置を維持
      if (satelliteData.length === 0) {
        viewer.zoomTo(viewer.entities);
      }
    }, 1000);

    // クリーンアップ関数
    return () => {
      if (tickListener) {
        viewer.clock.onTick.removeEventListener(tickListener);
      }
      viewer.destroy();
    };
  }, [tles, useEarthFixed]);

  if (tles.length === 0) {
    return (
      <Typography
        style={{ textAlign: "center", marginTop: "20px", color: "red" }}
      >
        軌道情報がありません
      </Typography>
    );
  }

  return (
    <div ref={cesiumContainer} style={{ width: "100%", height: "100vh" }} />
  );
};

export default App;

// テスト用にユーティリティ関数をエクスポート
export { calculateOrbitalPoints };
